'use client';

import gsap from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
import {useGSAP} from '@gsap/react';
import {motion, useScroll, useTransform} from 'motion/react';
import {LenisRef, ReactLenis} from 'lenis/react';
import {useEffect, useLayoutEffect, useRef} from 'react';

// eslint-disable-next-line udemy/no-css-modules
import 'lenis/dist/lenis.css';

import styles from './film-strip.module.scss';

gsap.registerPlugin(useGSAP);
gsap.registerPlugin(ScrollTrigger);

const marqueeTexts = [
    'Instructor tools',
    'Site redesigns',
    'Live learning events',
    'Course experience updates',
    'New learning formats',
    'And more!',
];

interface FilmStripItem {
    id: string;
    title: string;
    image: string;
    color?: string;
}

interface FilmStripProps {
    items: FilmStripItem[];
}

export function FilmStrip({items}: FilmStripProps) {
    const containerRef = useRef<HTMLDivElement>(null!);
    const lenisRef = useRef<LenisRef>(null!);
    const marqueeRef = useRef<HTMLDivElement>(null!);
    const {scrollYProgress} = useScroll({target: containerRef, offset: ['start end', 'end start']});
    const marqueeX = useTransform(scrollYProgress, [0, 1], [2300, -2800]);
    const marqueeOpacity = useTransform(scrollYProgress, [0, 0.01, 0.99, 1], [0, 1, 1, 0]);

    useEffect(() => {
        function update(time: number) {
            lenisRef.current?.lenis?.raf(time * 1000);
        }

        gsap.ticker.add(update);
        gsap.ticker.lagSmoothing(0);

        return () => gsap.ticker.remove(update);
    }, []);

    return (
        <ReactLenis root options={{autoRaf: false, lerp: 0.12}} ref={lenisRef}>
            <section className={styles.filmStrip}>
                <div ref={containerRef} className={styles.grid}>
                    <div className={styles.gridContainer}>
                        {items.map((item, i) => (
                            <FilmStripCard key={item.id} item={item} index={i} />
                        ))}
                    </div>
                </div>
                <div className={styles.marqueeText}>
                    <motion.div
                        className={styles.marqueeInner}
                        ref={marqueeRef}
                        style={{x: marqueeX, opacity: marqueeOpacity}}
                    >
                        {marqueeTexts.map((text, i) => (
                            <span key={i}>{text}</span>
                        ))}
                    </motion.div>
                </div>
            </section>
        </ReactLenis>
    );
}

interface FilmStripCardProps {
    item: FilmStripItem;
    index: number;
}

function FilmStripCard({item, index}: FilmStripCardProps) {
    const wrapperRef = useRef<HTMLDivElement>(null!);
    const imageRef = useRef<HTMLDivElement>(null!);
    const leftSide = index % 2 === 0;

    useLayoutEffect(() => {
        const timeline = gsap
            .timeline({
                scrollTrigger: {
                    trigger: wrapperRef.current,
                    start: 'top bottom+=10%', // Start when the top of the element is 10% past the bottom of the viewport
                    end: 'bottom top-=25%', // End when the bottom of the element is 25% past the top of the viewport
                    scrub: true,
                },
            })
            .from(wrapperRef.current, {
                startAt: {filter: 'blur(0px) brightness(100%) contrast(100%)'}, // Ensure no blur or brightness adjustments at the start
                z: 300, // Translate the item 300px closer on the Z-axis
                rotateX: 70, // Start with a rotation of 70 degrees on the X-axis
                rotateZ: leftSide ? 5 : -5, // Rotate 5 degrees if on the left, -5 degrees if on the right
                xPercent: leftSide ? -40 : 40, // Horizontal translation: -40% if on the left, 40% if on the right
                skewX: leftSide ? -20 : 20, // Skew the element on the X-axis
                yPercent: 100, // Start with the element below the viewport
                filter: 'blur(7px) brightness(0%) contrast(400%)', // Start with a blur, low brightness, and high contrast
                ease: 'sine',
            })
            .to(wrapperRef.current, {
                z: 300, // Move back to original Z-translation (300px)
                rotateX: -50, // Rotate -50 degrees on the X-axis
                rotateZ: leftSide ? -1 : 1, // Slightly rotate on the Z-axis (-1 or 1 depending on side)
                xPercent: leftSide ? -20 : 20, // Move slightly left (-20%) or right (20%) on exit
                skewX: leftSide ? 10 : -10, // Skew slightly on exit
                filter: 'blur(4px) brightness(0%) contrast(500%)', // Add blur and reduce brightness on exit
                ease: 'sine.in',
            })
            .from(
                imageRef.current,
                {
                    // Additional animation on the image itself (scale along the Y-axis)
                    scaleY: 1.8, // Scale Y-axis by 1.8
                    ease: 'sine',
                },
                0,
            )
            .to(
                imageRef.current,
                {
                    scaleY: 1.8, // Return to normal scaling
                    ease: 'sine.in',
                },
                '>',
            );

        return () => {
            timeline.kill();
        };
    }, [index, leftSide]);

    return (
        <figure key={index} className={styles.gridItem}>
            <div className={styles.imageContainer} ref={wrapperRef}>
                <div
                    className={styles.image}
                    ref={imageRef}
                    style={{backgroundImage: `url(${item.image})`}}
                ></div>
            </div>
        </figure>
    );
}

export type {FilmStripProps, FilmStripItem};
